import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Paper from '@mui/material/Paper';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { emailFrequencies } from 'shared/constants/news/emailFrequencies';
import { emailNotificationsParser } from 'shared/parsers';
import { emailNotificationsTypes } from 'shared/constants/news/emailNotificationsTypes';

const useStyle = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3, 2),
  },
  space: {
    marginBottom: theme.spacing(2),
  },
  label: {
    fontSize: 14,
  },
  formControl: {
    width: '100%',
  },
}));

const initialValues = {
  frequency: emailFrequencies.DISABLED,
  portfolioUploadCompleted: false,
  ratingIsAvailable: false,
  followedAnalystPublishesAnAnnouncement: false,
  watchlistChanges: false,
};

const EmailNotifications = ({ pristine, emailSettingsChanged }) => {
  const classes = useStyle();
  const { t } = useTranslation();

  const { preferences } = useSelector(({ newsFeed }) => newsFeed);
  const [data, setData] = useState(emailNotificationsParser(preferences));
  const disabled = data.frequency === initialValues.frequency;

  const handleFrequencyChange = event => {
    setData({ ...data, frequency: event.target.value });
  };

  const handleTypeChange = type => event => {
    setData({
      ...data,
      type: event.target.checked ? data.type.concat(type) : data.type.filter(el => el !== type),
    });
  };

  useEffect(() => {
    setData(emailNotificationsParser(preferences));
  }, [preferences]);

  useEffect(() => {
    if (pristine) {
      setData(emailNotificationsParser(preferences));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pristine]);

  useEffect(() => {
    emailSettingsChanged(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const typeSelected = type => data && data.type && data.type.includes(type);

  return (
    <Paper className={classes.root}>
      <FormControl className={classes.formControl} component="fieldset">
        <FormLabel component="legend" className={classes.label}>
          {t('Frequency')}
        </FormLabel>
        <RadioGroup
          className={classes.space}
          aria-label="frequency"
          name="frequency"
          value={data.frequency}
          onChange={handleFrequencyChange}
          row
        >
          <FormControlLabel
            value={emailFrequencies.IMMEDIATE}
            control={<Radio color="primary" />}
            label={t('Immediate')}
          />
          <FormControlLabel
            value={emailFrequencies.DAILY_DIGEST}
            control={<Radio color="primary" />}
            label={t('Daily digest')}
          />
          <FormControlLabel
            value={emailFrequencies.DISABLED}
            control={<Radio color="primary" />}
            label={t('No notifications')}
          />
        </RadioGroup>
      </FormControl>
      <FormControl className={classes.formControl} component="fieldset">
        <FormLabel component="legend" className={classes.label}>
          {t('Type')}
        </FormLabel>
        <FormGroup>
          <FormControlLabel
            disabled={disabled}
            control={
              <Checkbox
                checked={typeSelected(emailNotificationsTypes.COMPLETED_ISIN_UPLOAD)}
                onChange={handleTypeChange(emailNotificationsTypes.COMPLETED_ISIN_UPLOAD)}
                value="portfolioUploadCompleted"
                color="primary"
              />
            }
            label={t('Portfolio upload completed')}
          />
          <FormControlLabel
            disabled={disabled}
            control={
              <Checkbox
                checked={typeSelected(emailNotificationsTypes.AVAILABLE_RATING)}
                onChange={handleTypeChange(emailNotificationsTypes.AVAILABLE_RATING)}
                value="ratingIsAvailable"
                color="primary"
              />
            }
            label={t('Rating is available')}
          />
          <FormControlLabel
            disabled={disabled}
            control={
              <Checkbox
                checked={typeSelected(
                  emailNotificationsTypes.AVAILABLE_ANNOUNCEMENT_FROM_FOLLOWED_ANALYST,
                )}
                onChange={handleTypeChange(
                  emailNotificationsTypes.AVAILABLE_ANNOUNCEMENT_FROM_FOLLOWED_ANALYST,
                )}
                value="followedAnalystPublishesAnAnnouncement"
                color="primary"
              />
            }
            label={t('Followed analyst publishes an announcement')}
          />
          <FormControlLabel
            disabled={disabled}
            control={
              <Checkbox
                checked={typeSelected(emailNotificationsTypes.WATCHLIST_UPDATE)}
                onChange={handleTypeChange(emailNotificationsTypes.WATCHLIST_UPDATE)}
                value="watchlistChanges"
                color="primary"
              />
            }
            label={t('Watchlist changes')}
          />
        </FormGroup>
      </FormControl>
    </Paper>
  );
};

EmailNotifications.defaultProps = {
  emailSettingsChanged: () => null,
  pristine: false,
};

EmailNotifications.propTypes = {
  emailSettingsChanged: PropTypes.func,
  pristine: PropTypes.bool,
};

export default EmailNotifications;
